// AIDEV-NOTE: single-source unit-size catalog; consumed by the Size Guide page (full render)
// and the About page stat (count only). Keep icon as a string key — no JSX in the entity layer;
// the UI maps the key to a component. Adding/removing a size updates the count everywhere.

export type UnitSizeCategory = 'small' | 'medium' | 'large'

/** Presentational hint resolved to an icon component in the UI layer. */
export type UnitSizeIcon = 'package' | 'home' | 'car'

// AIDEV-NOTE: named UnitSizeCatalogEntry (not UnitSize) to avoid colliding with the
// width/length/height UnitSize in ./types — both are re-exported from the entity barrel.
export interface UnitSizeCatalogEntry {
  id: string
  dimensions: string
  squareFeet: number
  cubicFeet: number
  description: string
  fits: string
  details: string
  category: UnitSizeCategory
  icon: UnitSizeIcon
}

export const UNIT_SIZES: UnitSizeCatalogEntry[] = [
  // Small Units
  {
    id: '5x5',
    dimensions: "5' x 5'",
    squareFeet: 25,
    cubicFeet: 200,
    description: 'Similar to a walk-in closet.',
    fits: 'One storage closet of stuff',
    details:
      'Perfect for storing seasonal items, office supplies, small furniture pieces, or about 10-15 boxes. Great for decluttering or storing items during a move. Climate controlled, interior access.',
    category: 'small',
    icon: 'package',
  },
  {
    id: '5x10',
    dimensions: "5' x 10'",
    squareFeet: 50,
    cubicFeet: 400,
    description: 'Similar to an average walk-in closet.',
    fits: 'Studio apartment of stuff',
    details:
      'Ideal for storing a mattress set, dresser, seasonal decorations, business inventory, or about 15-20 boxes. Great for college students or small apartment dwellers. Climate controlled, interior access.',
    category: 'small',
    icon: 'package',
  },
  // Medium Units
  {
    id: '10x7.5',
    dimensions: "10' x 7.5'",
    squareFeet: 75,
    cubicFeet: 600,
    description: 'Similar to a large walk-in closet.',
    fits: 'Large studio apartment of stuff',
    details:
      'Perfect for storing furniture from a large bedroom or living room, including sofas, dining sets, and appliances. Ideal for apartment contents or business storage. Climate controlled, interior access.',
    category: 'medium',
    icon: 'home',
  },
  {
    id: '10x10',
    dimensions: "10' x 10'",
    squareFeet: 100,
    cubicFeet: 800,
    description: 'Similar to a small bedroom or half of a one-car garage.',
    fits: '2 bedrooms of stuff',
    details:
      'Our most popular size! Can store furniture from a 1-2 bedroom apartment, including major appliances, or serve as commercial storage for inventory and equipment. Available in climate controlled interior and drive-up exterior options.',
    category: 'medium',
    icon: 'home',
  },
  {
    id: '10x12.5',
    dimensions: "10' x 12.5'",
    squareFeet: 125,
    cubicFeet: 1000,
    description: 'Similar to a small spare bedroom.',
    fits: '1 to 2 bedroom home of stuff',
    details:
      'Accommodates furniture and belongings from a 2-bedroom apartment, including large appliances, furniture sets, and numerous boxes. Great for family storage needs. Climate controlled, interior access.',
    category: 'medium',
    icon: 'home',
  },
  // Large Units
  {
    id: '10x15',
    dimensions: "10' x 15'",
    squareFeet: 150,
    cubicFeet: 1200,
    description:
      'Similar to a spare bedroom or two-thirds of a standard one-car garage.',
    fits: '2 bedroom home of stuff',
    details:
      'Perfect for storing contents of a 2-3 bedroom home, including large furniture, appliances, and personal belongings. Ideal for military deployments or major relocations. Available in climate controlled interior and drive-up exterior options.',
    category: 'large',
    icon: 'home',
  },
  {
    id: '10x17.5',
    dimensions: "10' x 17.5'",
    squareFeet: 175,
    cubicFeet: 1400,
    description: 'Similar to a large garage.',
    fits: '2 bedroom home of stuff with room for belongings from an outdoor shed',
    details:
      'Accommodates contents of a 2-3 bedroom home with room for additional items. Great for long-term storage during extended travel or home construction projects. Climate controlled, interior access.',
    category: 'large',
    icon: 'home',
  },
  {
    id: '10x20',
    dimensions: "10' x 20'",
    squareFeet: 200,
    cubicFeet: 1600,
    description: 'Similar to a standard one-car garage.',
    fits: '3 bedroom home of stuff',
    details:
      'Can store entire home contents including large furniture, appliances, and personal items. Also suitable for vehicle storage or large business inventory. Available in climate controlled interior and drive-up exterior options.',
    category: 'large',
    icon: 'car',
  },
  {
    id: '10x30',
    dimensions: "10' x 30'",
    squareFeet: 300,
    cubicFeet: 2400,
    description: 'Similar to a large garage or small warehouse.',
    fits: 'Fully furnished 4 bedroom home and garage',
    details:
      'Excellent for storing contents of large homes, RVs, boats, or substantial business inventory. Provides ample space for vehicles and equipment. Drive-up accessible.',
    category: 'large',
    icon: 'car',
  },
  {
    id: '10x40',
    dimensions: "10' x 40'",
    squareFeet: 400,
    cubicFeet: 3200,
    description: 'Similar to a large warehouse or commercial space.',
    fits: 'Four-bedroom house, garage, and bulky outdoor items',
    details:
      'Our largest unit size, perfect for commercial use, multiple vehicle storage, or storing contents from multiple homes. Popular for business storage. Drive-up accessible.',
    category: 'large',
    icon: 'car',
  },
]
